import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpRequest, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';


import { API_URL } from '../utils/tokens';
import { EntityService } from './entity.service';
import { map, publishReplay, refCount, take } from 'rxjs/operators';
import { ValueCheckingMode } from 'json2typescript/src/json2typescript/json-convert-enums';
import { JsonConvert } from 'json2typescript';

export interface HttpRequestOptions {
    headers?: HttpHeaders | {
        [header: string]: string | string[];
    };
    params?: HttpParams | {
        [param: string]: string | string[];
    };
    reportProgress?: boolean;
    responseType?: 'json';
    withCredentials?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class HttpService {
    protected http: HttpClient;
    protected entityService: EntityService;

    protected cache: Map<string, Observable<any>> = new Map();

    protected jsonConvert = new JsonConvert();
    public apiUrl: string;

    constructor(
        protected injector: Injector
    ) {
        this.apiUrl = injector.get(API_URL);

        this.http = injector.get(HttpClient);
        this.entityService = injector.get(EntityService);
        this.jsonConvert.valueCheckingMode = ValueCheckingMode.ALLOW_NULL;
        this.jsonConvert.ignorePrimitiveChecks = false;
    }

    public doHttpGet<T>(url: string): Observable<T> {
        return this.http
            .get<T>(`${this.apiUrl}${url}`)
    }

    public getData(url: string, classReference: new() => any): Observable<any> {
        return this.doHttpGet(url)
            .pipe(
                map(responseData => {
                    return this.fromJson(responseData, classReference);
                })
            )
    }

    public doHttpPost<T>(url: string, data: object, options?: HttpRequestOptions & {
        observe?: 'body';
    }): Observable<T> {
        return this.http.post<T>(`${this.apiUrl}${url}`, data, options)
    }

    public doHttpPostWithEvents<T>(url: string, data: object, options: HttpRequestOptions & {
        observe: 'events';
        reportProgress?: boolean;
    }): Observable<HttpEvent<T>> {
        return this.http.post<T>(`${this.apiUrl}${url}`, data, options)
    }

    public postData(url: string, data: object, classReference: new() => any, useConverter: boolean = false): Observable<any> {
        return this.http
            .post<any>(`${this.apiUrl}${url}`, useConverter ? this.toJson(data) : data)
            .pipe(
                map(responseData => {
                    return this.fromJson(responseData, classReference);
                })
            )
    }

    public doHttpPut<T>(url: string, data: object): Observable<T> {
        return this.http
            .put<T>(`${this.apiUrl}${url}`, data)
    }

    public putData(url: string, data: object, classReference: new() => any, useConverter: boolean = false): Observable<any> {
        return this
            .doHttpPut<any>(url, useConverter ? this.toJson(data) : data)
            .pipe(
                map(responseData => {
                    return this.fromJson(responseData, classReference);
                })
            )
    }

    public doHttpDelete<T>(url: string): Observable<T> {
        return this.http
            .delete<T>(`${this.apiUrl}${url}`)
    }

    public deleteData(url: string, classReference: new() => any): Observable<any> {
        return this.http
            .delete<any>(`${this.apiUrl}${url}`)
            .pipe(
                map(responseData => {
                    return this.fromJson(responseData, classReference);
                })
            )
    }

    public sendFormData(url: string, data: object, classReference: new() => any): Observable<any> {
        const body = new URLSearchParams();

        Object.keys(data).forEach(key => {
            body.set(key, data[key]);
        });

        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded'
        });

        return this.http
            .post(`${this.apiUrl}${url}`, body.toString(), {
                headers
            })
            .pipe(
                map(responseData => {
                    return this.fromJson(responseData, classReference);
                })
            )
    }

    public fromJsonArray<T>(object: any, classReference: new() => T): T[] {
        return this.jsonConvert.deserialize(object, classReference) as T[];
    }

    public fromJson<T>(object: any, classReference: new() => T): T {
        return this.jsonConvert.deserialize(object, classReference) as T;
    }

    public toJson<T>(object: T): any {
        return this.jsonConvert.serialize<T>(object);
    }

}
